
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import ChatWidget from './ChatWidget';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { triggerLisaChat } from '@/utils/onboardingEvents';

const ChatButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [connectionError, setConnectionError] = useState(false);
  const [messageInProgress, setMessageInProgress] = useState(false);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [isConnectionTesting, setIsConnectionTesting] = useState(false);

  useEffect(() => {
    const animationInterval = setInterval(() => {
      setAnimationKey(prev => prev + 1);
    }, 10000);
    
    return () => clearInterval(animationInterval);
  }, []);

  useEffect(() => {
    const handleLisaChat = () => {
      console.log('Received lisa-chat event, opening chat');
      setIsOpen(true);
    };
    
    const handleConnectionError = () => {
      console.error('Chat connection error');
      setConnectionError(true);
      toast.error('Problema de conexión con Lisa. Intenta de nuevo más tarde.');
    };
    
    const handleMessageInProgress = (event: CustomEvent) => {
      console.log('Message in progress status:', event.detail?.status);
      setMessageInProgress(event.detail?.status || false);
    };
    
    const handleDailyLimitReached = () => {
      console.log('Daily message limit reached');
      setDailyLimitReached(true);
      toast.warning('Has alcanzado tu límite diario de preguntas a Lisa. Vuelve mañana.');
    };
    
    window.addEventListener('lisa-chat', handleLisaChat);
    window.addEventListener('lisa-connection-error', handleConnectionError as EventListener);
    window.addEventListener('lisa-message-in-progress', handleMessageInProgress as EventListener);
    window.addEventListener('lisa-daily-limit-reached', handleDailyLimitReached as EventListener);
    
    return () => {
      window.removeEventListener('lisa-chat', handleLisaChat);
      window.removeEventListener('lisa-connection-error', handleConnectionError as EventListener);
      window.removeEventListener('lisa-message-in-progress', handleMessageInProgress as EventListener);
      window.removeEventListener('lisa-daily-limit-reached', handleDailyLimitReached as EventListener);
    };
  }, []);

  const toggleChat = async () => {
    console.log('Toggle chat button clicked, current state:', isOpen);
    
    if (isConnectionTesting) {
      return;
    }
    
    if (isOpen && messageInProgress) {
      toast.warning('Espera a que el mensaje termine de enviarse');
      return;
    }
    
    if (!isOpen) {
      setConnectionError(false);
      setDailyLimitReached(false);
      setIsConnectionTesting(true);
      
      try {
        const { error } = await supabase.from('profiles')
          .select('count', { count: 'exact', head: true })
          .limit(1);
          
        if (error) {
          console.error("Supabase connection test failed:", error);
          setConnectionError(true);
          toast.error('No se pudo conectar con el servicio. Intenta más tarde.');
          setIsConnectionTesting(false);
          return;
        }
        
        setIsOpen(true);
        // Trigger the lisa-chat event when the chat is opened
        triggerLisaChat();
      } catch (error) {
        console.error("Connection test failed:", error);
        setConnectionError(true);
        toast.error('Error de conexión. Intenta más tarde.');
      } finally {
        setIsConnectionTesting(false);
      }
    } else {
      setIsOpen(false);
    }
  };

  const buttonVariants = {
    wiggle: {
      rotate: [-3, 3],
      transition: { 
        duration: 1.5, 
        repeat: 2, 
        repeatType: "reverse" as const,
        type: "tween" as const 
      }
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed bottom-24 right-6 z-50 sm:bottom-24 sm:right-8 md:right-12"
          >
            <ChatWidget onClose={() => !messageInProgress && setIsOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed bottom-6 right-6 z-50 sm:bottom-8 sm:right-8 md:right-12">
        <motion.div 
          key={animationKey}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          variants={buttonVariants}
          animate="wiggle"
        >
          <Button
            onClick={toggleChat}
            className={`rounded-full shadow-lg flex items-center justify-center w-14 h-14 ${
              isOpen ? 'bg-red-500 hover:bg-red-600' : 'bg-vegana-primary hover:bg-vegana-primary/90'
            } ${messageInProgress || isConnectionTesting ? 'opacity-70 cursor-not-allowed' : ''}`}
            aria-label={isOpen ? "Cerrar chat con Lisa" : "Abrir chat con Lisa"}
            disabled={messageInProgress || isConnectionTesting}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Avatar className="h-10 w-10 border-2 border-white">
                <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                <AvatarFallback>L</AvatarFallback>
              </Avatar>
            )}
          </Button>
        </motion.div>
      </div>
    </>
  );
};

export default ChatButton;
